import { useState, useEffect, useRef, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { Label } from "@/components/ui/label";
import { Video, VideoOff, Mic, MicOff, SwitchCamera, Users } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useIsMobile } from "@/hooks/use-mobile";

const DEVICE_PREF_KEY = "meethub_device_prefs";

type DevicePrefs = {
  micId?: string;
  speakerId?: string;
  cameraId?: string;
};

const getInitialDevicePrefs = (): DevicePrefs => {
  try {
    const raw = localStorage.getItem(DEVICE_PREF_KEY);
    if (raw) return JSON.parse(raw) as DevicePrefs;
  } catch (error) {
    console.warn("Failed to parse device prefs", error);
  }

  return {
    micId: localStorage.getItem("preferredMicId") || undefined,
    speakerId: localStorage.getItem("preferredSpeakerId") || undefined,
    cameraId: localStorage.getItem("preferredCameraId") || undefined,
  };
};

interface PreJoinScreenProps {
  meetingTitle: string;
  onJoin: (videoEnabled: boolean, audioEnabled: boolean, stream: MediaStream | null, guestName?: string) => void;
  requiresPassword?: boolean;
  meetingCode?: string;
  joinDisabled?: boolean;
  scheduledStartTime?: Date | null;
}

export function PreJoinScreen({
  meetingTitle,
  onJoin,
  requiresPassword,
  meetingCode,
  joinDisabled = false,
  scheduledStartTime,
}: PreJoinScreenProps) {
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const [isVideoEnabled, setIsVideoEnabled] = useState(true);
  const [isAudioEnabled, setIsAudioEnabled] = useState(true);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [permissionGranted, setPermissionGranted] = useState(false);
  const [guestName, setGuestName] = useState("");
  const [password, setPassword] = useState("");
  const [facingMode, setFacingMode] = useState<"user" | "environment">("user");
  const [availableMics, setAvailableMics] = useState<MediaDeviceInfo[]>([]);
  const [availableSpeakers, setAvailableSpeakers] = useState<MediaDeviceInfo[]>([]);
  const [availableCameras, setAvailableCameras] = useState<MediaDeviceInfo[]>([]);
  const initialPrefs = useRef<DevicePrefs>(getInitialDevicePrefs()).current;

  const [selectedMicId, setSelectedMicId] = useState<string | undefined>(() => initialPrefs.micId);
  const [selectedSpeakerId, setSelectedSpeakerId] = useState<string | undefined>(() => initialPrefs.speakerId);
  const [selectedCameraId, setSelectedCameraId] = useState<string | undefined>(() => initialPrefs.cameraId);
  const [deviceWarning, setDeviceWarning] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const permissionsRequestedRef = useRef(false);

  const scheduledStartLabel = scheduledStartTime
    ? scheduledStartTime.toLocaleString()
    : null;

  useEffect(() => {
    loadDevices();

    const checkExistingPermissions = async () => {
      try {
        const cameraPermission = await navigator.permissions?.query({ name: "camera" as PermissionName });
        const micPermission = await navigator.permissions?.query({ name: "microphone" as PermissionName });

        if (cameraPermission?.state === "granted" || micPermission?.state === "granted") {
          await requestPermissions();
        }
      } catch (error) {
        console.warn("Permission query unavailable", error);
      }
    };

    checkExistingPermissions();

    // Pre-fill guest name from sessionStorage if available
    if (!user) {
      const storedGuestName = sessionStorage.getItem('guestName');
      if (storedGuestName) {
        setGuestName(storedGuestName);
      }
    }

    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, [user]);

  useEffect(() => {
    if (!stream) return;
    stream.getVideoTracks().forEach(track => {
      track.enabled = isVideoEnabled;
    });
  }, [isVideoEnabled, stream]);

  useEffect(() => {
    if (!stream) return;
    stream.getAudioTracks().forEach(track => {
      track.enabled = isAudioEnabled;
    });
  }, [isAudioEnabled, stream]);

  const loadDevices = useCallback(async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const mics = devices.filter(device => device.kind === "audioinput");
      const speakers = devices.filter(device => device.kind === "audiooutput");
      const cameras = devices.filter(device => device.kind === "videoinput");

      setAvailableMics(mics);
      setAvailableSpeakers(speakers);
      setAvailableCameras(cameras);

      let warning: string | null = null;

      if (!selectedMicId && mics[0]) {
        setSelectedMicId(mics[0].deviceId);
      } else if (selectedMicId && !mics.some((mic) => mic.deviceId === selectedMicId)) {
        warning = "Selected microphone is unavailable. Switched to default.";
        setSelectedMicId(mics[0]?.deviceId);
      }

      if (!selectedSpeakerId && speakers[0]) {
        setSelectedSpeakerId(speakers[0].deviceId);
      } else if (selectedSpeakerId && !speakers.some((speaker) => speaker.deviceId === selectedSpeakerId)) {
        warning = warning || "Selected speaker is unavailable. Switched to default.";
        setSelectedSpeakerId(speakers[0]?.deviceId);
      }

      if (!selectedCameraId && cameras[0]) {
        setSelectedCameraId(cameras[0].deviceId);
      } else if (selectedCameraId && !cameras.some((camera) => camera.deviceId === selectedCameraId)) {
        warning = warning || "Selected camera is unavailable. Switched to default.";
        setSelectedCameraId(cameras[0]?.deviceId);
      }

      setDeviceWarning(warning);
    } catch (error) {
      console.error("Error loading devices:", error);
    }
  }, [selectedCameraId, selectedMicId, selectedSpeakerId]);

  const buildConstraints = useCallback(
    (videoEnabled: boolean, audioEnabled: boolean) => ({
      video: videoEnabled
        ? {
            width: 1280,
            height: 720,
            facingMode,
            ...(selectedCameraId ? { deviceId: { exact: selectedCameraId } } : {}),
          }
        : false,
      audio: audioEnabled
        ? {
            ...(selectedMicId ? { deviceId: { exact: selectedMicId } } : {}),
          }
        : false,
    }),
    [facingMode, selectedCameraId, selectedMicId]
  );

  const persistDevicePrefs = useCallback((prefs: DevicePrefs) => {
    const nextPrefs: DevicePrefs = {
      micId: prefs.micId ?? selectedMicId,
      speakerId: prefs.speakerId ?? selectedSpeakerId,
      cameraId: prefs.cameraId ?? selectedCameraId,
    };

    try {
      localStorage.setItem(DEVICE_PREF_KEY, JSON.stringify(nextPrefs));
    } catch (error) {
      console.warn("Failed to persist device prefs", error);
    }
  }, [selectedCameraId, selectedMicId, selectedSpeakerId]);

  const requestPermissions = useCallback(async () => {
    if (permissionsRequestedRef.current) return;
    permissionsRequestedRef.current = true;

    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia(buildConstraints(true, true));

      mediaStream.getVideoTracks().forEach(track => {
        track.enabled = isVideoEnabled;
      });
      mediaStream.getAudioTracks().forEach(track => {
        track.enabled = isAudioEnabled;
      });

      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }

      setStream(mediaStream);
      setPermissionGranted(true);

      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }

      await loadDevices();
    } catch (error) {
      console.error("Error requesting permissions:", error);
      setPermissionGranted(false);
      permissionsRequestedRef.current = false;
    }
  }, [buildConstraints, isAudioEnabled, isVideoEnabled, loadDevices, stream]);

  const toggleVideo = async () => {
    const newState = !isVideoEnabled;
    setIsVideoEnabled(newState);

    if (newState && !permissionGranted) {
      await requestPermissions();
    }
  };

  const toggleAudio = async () => {
    const newAudioState = !isAudioEnabled;
    setIsAudioEnabled(newAudioState);

    if (newAudioState && !permissionGranted) {
      await requestPermissions();
    }
  };

  useEffect(() => {
    if (!permissionGranted) return;

    const updateStreamForDevices = async () => {
      try {
        const mediaStream = await navigator.mediaDevices.getUserMedia(
          buildConstraints(isVideoEnabled, true)
        );

        mediaStream.getAudioTracks().forEach(track => {
          track.enabled = isAudioEnabled;
        });
        mediaStream.getVideoTracks().forEach(track => {
          track.enabled = isVideoEnabled;
        });

        if (stream) {
          stream.getTracks().forEach(track => track.stop());
        }

        setStream(mediaStream);
        if (videoRef.current) {
          videoRef.current.srcObject = mediaStream;
        }
      } catch (error) {
        console.error("Error updating stream for selected devices:", error);
      }
    };

    updateStreamForDevices();
  }, [buildConstraints, isAudioEnabled, isVideoEnabled, permissionGranted, selectedCameraId, selectedMicId]);

  useEffect(() => {
    persistDevicePrefs({ micId: selectedMicId });
  }, [persistDevicePrefs, selectedMicId]);

  useEffect(() => {
    persistDevicePrefs({ speakerId: selectedSpeakerId });
  }, [persistDevicePrefs, selectedSpeakerId]);

  useEffect(() => {
    persistDevicePrefs({ cameraId: selectedCameraId });
  }, [persistDevicePrefs, selectedCameraId]);

  const switchCamera = async () => {
    if (!stream) return;
    
    // Stop current video tracks
    stream.getVideoTracks().forEach(track => track.stop());
    
    // Toggle facing mode
      const newFacingMode = facingMode === "user" ? "environment" : "user";
      setFacingMode(newFacingMode);
    
    try {
      // Get new stream with opposite camera
      const newStream = await navigator.mediaDevices.getUserMedia({
        video: { 
          width: 1280, 
          height: 720,
          facingMode: newFacingMode
        },
        audio: true
      });
      
      // Keep audio tracks from original stream
      const audioTracks = stream.getAudioTracks();
      audioTracks.forEach(track => {
        newStream.addTrack(track);
      });
      
      setStream(newStream);
      
      if (videoRef.current) {
        videoRef.current.srcObject = newStream;
      }
    } catch (error) {
      console.error("Error switching camera:", error);
      // Revert facing mode if switching fails
      setFacingMode(facingMode);
    }
  };

  const handleJoin = async () => {
    // Validate guest name if not logged in
    if (!user && !guestName.trim()) {
      return;
    }

    if (joinDisabled) {
      return;
    }

    if (!permissionGranted && !permissionsRequestedRef.current) {
      await requestPermissions();
    }

    const joinWithFallback = (mediaStream: MediaStream | null, video: boolean, audio: boolean) => {
      onJoin(video, audio, mediaStream, user ? undefined : guestName);
    };

    if (!permissionGranted) {
      joinWithFallback(null, false, false);
      return;
    }

    // Pass the stream to the meeting room instead of stopping it
    joinWithFallback(stream, isVideoEnabled, isAudioEnabled);
  };

  const isJoinDisabled = joinDisabled || (!user && !guestName.trim());

  return (
    <div className="min-h-screen bg-background relative overflow-hidden flex items-center justify-center p-3 sm:p-4 md:p-6 lg:p-8">
      {/* Animated background gradients */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-1/2 -left-1/2 w-full h-full bg-gradient-primary opacity-20 blur-3xl rounded-full animate-pulse" />
        <div className="absolute -bottom-1/2 -right-1/2 w-full h-full bg-gradient-secondary opacity-20 blur-3xl rounded-full animate-pulse" style={{ animationDelay: '1s' }} />
      </div>

      <Card className="w-full max-w-4xl relative z-10 shadow-card backdrop-blur-sm bg-card/95 border-border/50 my-4 sm:my-6">
        <CardHeader className="text-center space-y-3 sm:space-y-4 pb-3 sm:pb-4 md:pb-6 px-3 sm:px-4 md:px-6">
          {/* Icon with gradient background */}
          <div className="flex justify-center">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-primary rounded-2xl blur-xl opacity-60 animate-pulse" />
              <div className="relative p-2.5 sm:p-3 bg-gradient-primary rounded-2xl shadow-glow">
                <Video className="h-6 w-6 sm:h-7 sm:w-7 md:h-8 md:w-8 text-primary-foreground" />
              </div>
            </div>
          </div>

          <div className="space-y-1 sm:space-y-2">
            <CardTitle className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold bg-gradient-primary bg-clip-text text-transparent">
              Ready to join?
            </CardTitle>
            <p className="text-xs sm:text-sm md:text-base lg:text-lg text-muted-foreground font-medium px-2">{meetingTitle}</p>
          </div>
        </CardHeader>
        
        <CardContent className="space-y-4 sm:space-y-5 md:space-y-6 lg:space-y-8 px-3 sm:px-4 md:px-6">
          <div className="grid gap-4 lg:gap-6 lg:grid-cols-[1.35fr_1fr] items-start">
            {/* Video Preview with enhanced styling */}
            <div className="space-y-3 sm:space-y-4">
              <div
                className="relative w-full bg-gradient-to-br from-muted/50 to-muted rounded-lg sm:rounded-xl overflow-hidden shadow-lg border border-border/50 group"
                style={{ aspectRatio: isMobile ? '3/4' : '16/9' }}
              >
                {isVideoEnabled && permissionGranted ? (
                  <>
                    <video
                      ref={videoRef}
                      className={`w-full h-full object-contain ${facingMode === 'user' ? 'scale-x-[-1]' : ''}`}
                      autoPlay
                      muted
                      playsInline
                    />
                    {isMobile && (
                      <Button
                        variant="secondary"
                        size="icon"
                        onClick={switchCamera}
                        className="absolute top-2 right-2 sm:top-3 sm:right-3 md:top-4 md:right-4 rounded-full h-9 w-9 sm:h-10 sm:w-10 md:h-12 md:w-12 shadow-lg hover:scale-110 transition-transform"
                        title="Switch camera"
                      >
                        <SwitchCamera className="h-4 w-4 sm:h-5 sm:w-5 md:h-6 md:w-6" />
                      </Button>
                    )}
                    {/* Video overlay border glow on hover */}
                    <div className="absolute inset-0 border-2 border-primary/0 group-hover:border-primary/30 rounded-lg sm:rounded-xl transition-all duration-300 pointer-events-none" />
                  </>
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-muted/30 to-muted/60 backdrop-blur-sm">
                    <div className="text-center space-y-2 sm:space-y-3 md:space-y-4 px-3 sm:px-4">
                      <div className="relative inline-block">
                        <div className="absolute inset-0 bg-destructive/20 rounded-full blur-xl" />
                        <VideoOff className="relative h-10 w-10 sm:h-12 sm:w-12 md:h-16 md:w-16 lg:h-20 lg:w-20 text-destructive/80 mx-auto" />
                      </div>
                      <p className="text-xs sm:text-sm md:text-base lg:text-lg text-muted-foreground font-medium">
                        {permissionGranted ? "Camera is off" : "Camera permission required"}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Controls with enhanced styling */}
              <div className="flex items-center justify-center gap-3 sm:gap-4 md:gap-5 lg:gap-6 py-1 sm:py-2">
                <div className="relative group">
                  <div className="absolute inset-0 bg-gradient-primary opacity-0 group-hover:opacity-20 rounded-full blur-xl transition-opacity" />
                  <Button
                    variant={isVideoEnabled ? "default" : "destructive"}
                    size="icon"
                    onClick={toggleVideo}
                    className="relative rounded-full h-12 w-12 sm:h-14 sm:w-14 md:h-16 md:w-16 shadow-lg hover:scale-110 transition-all"
                    disabled={!permissionGranted}
                  >
                    {isVideoEnabled ? <Video className="h-4 w-4 sm:h-5 sm:w-5 md:h-6 md:w-6" /> : <VideoOff className="h-4 w-4 sm:h-5 sm:w-5 md:h-6 md:w-6" />}
                  </Button>
                </div>

                <div className="relative group">
                  <div className="absolute inset-0 bg-gradient-primary opacity-0 group-hover:opacity-20 rounded-full blur-xl transition-opacity" />
                  <Button
                    variant={isAudioEnabled ? "default" : "destructive"}
                    size="icon"
                    onClick={toggleAudio}
                    className="relative rounded-full h-12 w-12 sm:h-14 sm:w-14 md:h-16 md:w-16 shadow-lg hover:scale-110 transition-all"
                    disabled={!permissionGranted}
                  >
                    {isAudioEnabled ? <Mic className="h-4 w-4 sm:h-5 sm:w-5 md:h-6 md:w-6" /> : <MicOff className="h-4 w-4 sm:h-5 sm:w-5 md:h-6 md:w-6" />}
                  </Button>
                </div>
              </div>
            </div>

            {/* Right column: quick setup */}
            <div className="space-y-3 sm:space-y-4 lg:space-y-5 p-3 sm:p-4 rounded-xl border border-border/50 bg-muted/40 shadow-inner">
              {/* Form inputs section with grid layout on laptop */}
              <div className="grid gap-3 sm:gap-4 grid-cols-1 md:grid-cols-2">
                {!user && (
                  <div className="space-y-1.5 sm:space-y-2">
                    <Label htmlFor="guestName" className="text-xs sm:text-sm font-medium">Your Name</Label>
                    <Input
                      id="guestName"
                      placeholder="Enter your name"
                      value={guestName}
                      onChange={(e) => setGuestName(e.target.value)}
                      maxLength={50}
                      className="h-9 sm:h-10 md:h-11 text-xs sm:text-sm md:text-base bg-background border-border/50 focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all"
                    />
                  </div>
                )}

                {requiresPassword && (
                  <div className={`${!user ? "md:col-span-1" : "md:col-span-2"} space-y-1.5 sm:space-y-2`}>
                    <Label htmlFor="password" className="text-xs sm:text-sm font-medium">Meeting Password</Label>
                    <PasswordInput
                      id="password"
                      placeholder="Enter meeting password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="h-9 sm:h-10 md:h-11 text-xs sm:text-sm md:text-base bg-background border-border/50 focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all"
                    />
                  </div>
                )}
              </div>

              {/* Device selectors */}
              <div className="space-y-2">
                <div className="grid sm:grid-cols-2 lg:grid-cols-1 gap-2 sm:gap-3 lg:gap-4">
                  <div className="space-y-1.5">
                    <Label className="text-xs sm:text-sm font-medium">Camera</Label>
                    <select
                      className="w-full rounded-md border border-border/60 bg-background px-2.5 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                      value={selectedCameraId || ""}
                      onChange={(event) => setSelectedCameraId(event.target.value || undefined)}
                      disabled={!permissionGranted}
                    >
                      {availableCameras.map(camera => (
                        <option key={camera.deviceId} value={camera.deviceId}>
                          {camera.label || "Camera"}
                        </option>
                      ))}
                      {availableCameras.length === 0 && <option value="">No camera found</option>}
                    </select>
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-xs sm:text-sm font-medium">Microphone</Label>
                    <select
                      className="w-full rounded-md border border-border/60 bg-background px-2.5 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                      value={selectedMicId || ""}
                      onChange={(event) => setSelectedMicId(event.target.value || undefined)}
                      disabled={!permissionGranted}
                    >
                      {availableMics.map(mic => (
                        <option key={mic.deviceId} value={mic.deviceId}>
                          {mic.label || "Microphone"}
                        </option>
                      ))}
                      {availableMics.length === 0 && <option value="">No microphone found</option>}
                    </select>
                  </div>

                  <div className="space-y-1.5 sm:col-span-2 lg:col-span-1">
                    <Label className="text-xs sm:text-sm font-medium">Speaker</Label>
                    <select
                      className="w-full rounded-md border border-border/60 bg-background px-2.5 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                      value={selectedSpeakerId || ""}
                      onChange={(event) => setSelectedSpeakerId(event.target.value || undefined)}
                      disabled={!permissionGranted}
                    >
                      {availableSpeakers.map(speaker => (
                        <option key={speaker.deviceId} value={speaker.deviceId}>
                          {speaker.label || "Speaker"}
                        </option>
                      ))}
                      {availableSpeakers.length === 0 && <option value="">No speaker found</option>}
                    </select>
                  </div>
                </div>

                {/* Quick device summary for mobile */}
                <div className="grid grid-cols-2 gap-2 sm:hidden">
                  <div className="rounded-lg border border-border/50 bg-background px-2 py-1.5 text-[11px] text-muted-foreground">
                    <span className="block text-[10px] uppercase tracking-wide text-muted-foreground/80">Camera</span>
                    <span className="font-medium text-foreground line-clamp-1">{availableCameras.find((c) => c.deviceId === selectedCameraId)?.label || 'Auto-detected'}</span>
                  </div>
                  <div className="rounded-lg border border-border/50 bg-background px-2 py-1.5 text-[11px] text-muted-foreground">
                    <span className="block text-[10px] uppercase tracking-wide text-muted-foreground/80">Mic</span>
                    <span className="font-medium text-foreground line-clamp-1">{availableMics.find((m) => m.deviceId === selectedMicId)?.label || 'Auto-detected'}</span>
                  </div>
                </div>
              </div>

              {deviceWarning && (
                <p className="text-xs sm:text-sm text-amber-600 bg-amber-50 border border-amber-200 rounded-md p-2">
                  {deviceWarning}
                </p>
              )}

              {scheduledStartLabel && (
                <div className="space-y-1 rounded-lg border border-border/50 bg-background px-3 py-2">
                  <p className="text-xs sm:text-sm text-muted-foreground">
                    Meeting starts at {scheduledStartLabel}
                  </p>
                  {joinDisabled && (
                    <p className="text-[11px] sm:text-xs text-muted-foreground">
                      We’ll unlock the join button once the scheduled time arrives.
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Join Button with gradient */}
          <Button
            onClick={handleJoin}
            className="w-full h-11 sm:h-12 md:h-14 text-xs sm:text-sm md:text-base lg:text-lg font-semibold bg-gradient-primary hover:opacity-90 transition-all shadow-glow hover:shadow-stream"
            disabled={isJoinDisabled}
          >
            <Users className="h-4 w-4 sm:h-5 sm:w-5 mr-1.5 sm:mr-2" />
            Join Meeting
          </Button>

          {/* Permission prompt with enhanced styling */}
          {!permissionGranted && (
            <div className="text-center space-y-2.5 sm:space-y-3 md:space-y-4 p-3 sm:p-4 md:p-6 bg-muted/30 rounded-lg border border-border/50">
              <p className="text-[10px] sm:text-xs md:text-sm lg:text-base text-muted-foreground px-2">
                Camera and microphone access is required to join the meeting
              </p>
              <Button
                variant="outline"
                onClick={requestPermissions}
                className="hover:bg-primary/10 hover:border-primary transition-all text-xs sm:text-sm md:text-base h-8 sm:h-9 md:h-10"
              >
                Grant Permissions
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
